import React, { useState, useEffect } from 'react';
import { Github } from 'lucide-react';
import { Button } from '@/ui/Button';
import { toast } from 'sonner';
import { supabaseService, type Repository, type InstallationStatus } from '@/shared/services/supabaseService';
import { RepositoryList } from '@/features/repository/components/RepositoryList';
import { Section } from '@/ui/layout/Section';
import { Skeleton } from "@/shared/components";

const RepositoriesTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [installationStatus, setInstallationStatus] = useState<InstallationStatus | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [reposData, installationData] = await Promise.all([
          supabaseService.getRepositories(),
          supabaseService.getInstallationStatus()
        ]);

        setRepositories(reposData);
        setInstallationStatus(installationData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load repositories');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleToggleReviewEnabled = async (repoId: string, enabled: boolean) => {
    try {
      await supabaseService.updateRepositorySettings(repoId, {
        reviewEnabled: enabled
      });

      setRepositories(prev =>
        prev.map(repo =>
          repo.id === repoId
            ? { ...repo, review_enabled: enabled }
            : repo
        )
      );

      toast.success(`Reviews ${enabled ? 'enabled' : 'disabled'} for repository`);
    } catch (error) {
      console.error('Error toggling review enabled:', error);
      toast.error('Failed to update repository settings');
    }
  };

  const handleConnectGitHub = () => {
    if (installationStatus?.installation_url) {
      window.open(installationStatus.installation_url, '_blank');
    }
  };

  if (loading) {
    return (
      <Section className="p-8">
         <Skeleton className="h-12 w-1/3 mb-8" />
         <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
         </div>
      </Section>
    );
  }

  return (
    <div className="space-y-8">
      {!installationStatus?.is_installed ? (
        <Section className="p-12 text-center" label="SETUP_REQUIRED">
            <div className="max-w-md mx-auto">
                <div className="mb-6 p-4 bg-neutral-100 dark:bg-neutral-900 inline-block border-2 border-neutral-950 dark:border-neutral-100">
                     <Github className="h-12 w-12" />
                </div>
                <h3 className="text-xl font-mono font-bold uppercase mb-4">Connect GitHub</h3>
                <p className="text-muted-foreground font-mono text-sm mb-8 leading-relaxed">
                  Install the Codii GitHub App to grant access to your repositories and start automated reviews.
                </p>
                <Button onClick={handleConnectGitHub} size="lg" className="rounded-none font-mono font-bold uppercase tracking-widest">
                    [ INSTALL_GITHUB_APP ]
                </Button>
            </div>
        </Section>
      ) : (
        <Section className="p-8 md:p-12" label="REPOSITORY_INDEX">
             <div className="flex justify-between items-center mb-8">
                 <div>
                    <h2 className="text-2xl font-bold font-mono uppercase">Your Repositories</h2>
                    <p className="text-muted-foreground font-mono text-xs mt-2 max-w-xl">
                        Manage review settings and rules for your connected codebases.
                    </p>
                 </div>
                 <Button onClick={handleConnectGitHub} variant="outline" className="rounded-none font-mono uppercase text-xs">
                     <Github className="mr-2 h-4 w-4" /> Sync / Add Repos
                 </Button>
             </div>

             <RepositoryList
                repositories={repositories}
                onToggleReview={handleToggleReviewEnabled}
             />
        </Section>
      )}
    </div>
  );
};

export default RepositoriesTab;
